import { addDays, format } from "date-fns";
import type React from "react";
import { useMemo } from "react";
import {
	getForecastDataPointsCount,
	getForecastDataPointsGraphql,
} from "@/api/services/forecastDataPointGraphqlService";
import {
	getForecastTransactionDistinctValues,
	getForecastTransactionsCount,
	getForecastTransactionsGraphql,
} from "@/api/services/forecastTransactionGraphqlService";
import type { ColumnFilters } from "@/components/MaximusTableReusable";
import { SCREEN_NAMES } from "@/constants/screenNames";
import { useTerminology } from "@/hooks/useTerminology";
import {
	getDemoForecastBalanceRows,
	getDemoForecastTransactionOptions,
	getDemoForecastTransactionRows,
} from "../../data/forecastTransactionsDemoData";
import { useBaseForecast } from "../../hooks/useBaseForecast";
import {
	type QuickFilterRanges,
	TransactionsBalancesTable,
	type TransactionsTableSource,
	type ViewMode,
} from "./TransactionsBalancesTable";

/**
 * This table lives under the 90-day forecast chart, so it must only ever show
 * the 90-day horizon.
 *
 * The two backing views constrain the horizon differently:
 * - vw_ForecastDataPoints (Balances) carries a real per-period `PeriodKey`
 *   (one daily result row per 90d/12m/3y run), so it is filtered by
 *   `periodKey = "90d"`.
 * - vw_ForecastTransactions has no period dimension — transactions are persisted
 *   once per run and span the full (up to 3-year) horizon — so it is instead
 *   constrained to the 90-day window that begins at the run's forecast start
 *   date (falling back to today when the start date is not yet known).
 */
const FORECAST_PERIOD_KEY = "90d";
const FORECAST_HORIZON_DAYS = 90;

/**
 * Day counts for the Transactions quick-filter buttons (7d / 30d / 90d). The
 * 90-day window intentionally mirrors {@link FORECAST_HORIZON_DAYS} so the
 * default quick filter matches the table's existing 90-day horizon exactly.
 */
const QUICK_FILTER_DAYS: Record<"7" | "30" | "90", number> = {
	"7": 7,
	"30": 30,
	"90": FORECAST_HORIZON_DAYS,
};

/**
 * Horizon constraint for the active view mode.
 *
 * Transactions are anchored to the forecast start date so the table matches the
 * chart and the Balances view (which is anchored via `periodKey`). When the
 * start date is not yet available we fall back to `nextXDays` from today, which
 * is equivalent to a run with no explicit start date.
 */
function horizonFilterFor(
	mode: ViewMode,
	startDate: Date | null,
): Record<string, unknown> {
	if (mode !== "transactions") {
		return { periodKey: { eq: FORECAST_PERIOD_KEY } };
	}
	if (!startDate) {
		return { forecastDate: { nextXDays: FORECAST_HORIZON_DAYS } };
	}
	return {
		forecastDate: {
			ge: format(startDate, "yyyy-MM-dd"),
			le: format(addDays(startDate, FORECAST_HORIZON_DAYS), "yyyy-MM-dd"),
		},
	};
}

interface ForecastTransactionsTableProps {
	/**
	 * General seam for opening the table pre-filtered (e.g. navigation from a
	 * dashboard/other page). Passed straight through to MaximusTableReusable as
	 * the visible filter seed and applied to the live query. Pass a
	 * stable/memoized object so it only re-seeds on an intentional change.
	 */
	initialColumnFilters?: ColumnFilters;
	/**
	 * Convenience for the same-page "View Transactions" click on the 90-day
	 * chart: when set, forces Transactions view and constrains the table to that
	 * single forecast day.
	 */
	dateFilter?: Date | null;
}

export const ForecastTransactionsTable: React.FC<
	ForecastTransactionsTableProps
> = ({ initialColumnFilters, dateFilter }) => {
	const { getForecastTerm } = useTerminology();
	// Anchor the transactions horizon on the run's forecast start date (first
	// point of the base timeline), matching the chart and the Balances view.
	const { baseForecast } = useBaseForecast();
	const horizonStartDate =
		baseForecast.length > 0 ? baseForecast[0].date : null;
	const horizonStartKey = horizonStartDate
		? format(horizonStartDate, "yyyy-MM-dd")
		: null;

	// Precomputed 7/30/90-day windows for the Transactions quick-filter buttons,
	// anchored on the forecast start date (falling back to today). The forecast
	// spans the start date plus the next N days, so an N-day window is inclusive
	// from the start date to `startDate + N` (N + 1 data points), matching the
	// chart. The 90-day window therefore equals the table's default horizon.
	const quickFilterRanges = useMemo<QuickFilterRanges>(() => {
		const startDate = horizonStartDate ?? new Date();
		const rangeForDays = (days: number) => ({
			ge: format(startDate, "yyyy-MM-dd"),
			le: format(addDays(startDate, days), "yyyy-MM-dd"),
		});
		return {
			"7": rangeForDays(QUICK_FILTER_DAYS["7"]),
			"30": rangeForDays(QUICK_FILTER_DAYS["30"]),
			"90": rangeForDays(QUICK_FILTER_DAYS["90"]),
		};
	}, [horizonStartDate]);

	const source = useMemo<TransactionsTableSource>(
		() => ({
			title: `${getForecastTerm(true)} Transactions`,
			screenName: SCREEN_NAMES.FORECAST_TRANSACTIONS,
			exportTransactionsForm: "ForecastTransactions",
			exportBalancesForm: "ForecastDataPoints",
			exportTransactionsFileName: "forecast-transactions-export",
			exportBalancesFileName: "forecast-balances-export",
			fetchTransactions: (filter, first, after, sort) =>
				getForecastTransactionsGraphql(filter, first, after, sort),
			fetchTransactionsCount: (filter) => getForecastTransactionsCount(filter),
			fetchDataPoints: (filter, first, after, sort) =>
				getForecastDataPointsGraphql(filter, first, after, sort),
			fetchDataPointsCount: (filter) => getForecastDataPointsCount(filter),
			fetchDistinctValues: (field) =>
				getForecastTransactionDistinctValues(field),
			getDemoTransactionRows: getDemoForecastTransactionRows,
			getDemoBalanceRows: getDemoForecastBalanceRows,
			getDemoTransactionOptions: getDemoForecastTransactionOptions,
			horizonFilter: (mode) => horizonFilterFor(mode, horizonStartDate),
			horizonKey: horizonStartKey,
		}),
		[horizonStartDate, horizonStartKey, getForecastTerm],
	);

	return (
		<TransactionsBalancesTable
			source={source}
			initialColumnFilters={initialColumnFilters}
			dateFilter={dateFilter}
			quickFilterRanges={quickFilterRanges}
		/>
	);
};
